
import { Component, Prop, Vue } from 'vue-property-decorator';

// Utils
import { Dictionary, isUndefined, omit } from 'lodash';
import { hasPermission } from '@/utils/Permissions';
import { hasSearch } from '@/utils/Helper';
import naturalSort from 'javascript-natural-sort';

// Enums
import PermissionsEnum from '@/ship/Enums/PermissionsEnum';
import AdministrationPageNamesEnum from '@/ship/Enums/AdministrationPageNamesEnum';

// Components
import VTable from '@/components/VTable.vue';
import VTableRow from '@/components/VTableRow.vue';
import VTableCell from '@/components/VTableCell.vue';
import VTableIconEdit from '@/components/VTableIconEdit.vue';
import VTableIconDelete from '@/components/VTableIconDelete.vue';
import VButtonIcon from '@/components/VButton/VButtonIcon.vue';
import StopSVG from '@/assets/stop_24px.svg';
import TrashSVG from '@/assets/trash_24px.svg';
import EditSVG from '@/assets/edit_24px.svg';
import SortArrowSVG from '@/assets/sort-arrow.svg';

@Component({
    components: {
        VTable,
        VTableRow,
        VTableCell,
        VTableIconEdit,
        VTableIconDelete,
        VButtonIcon,
        StopSVG,
        TrashSVG,
        EditSVG,
        SortArrowSVG,
    },
})
export default class VAdministrationPrimaryTable extends Vue {
    @Prop({ type: String, required: true })
    public page!: AdministrationPageNamesEnum;

    @Prop({ type: Array, required: true })
    public head!: string[];

    @Prop({ type: Array, required: true })
    public rows!: Array<Dictionary<string | number | number[]>>;

    @Prop({ type: String, default: '' })
    public search!: string;

    @Prop({ type: [Number, Object], default: null })
    public searchCityId!: number | null;

    readonly idColumnWidth = '70px';
    readonly actionsColumnWidth = '150px';

    public sortState = {
        field: 'id',
        direction: 'asc',
    };

    get filteredRows() {
        const filteredRows = this.rows.filter((row) => this.filterBySearch(row) && this.filterByCity(row));
        const sortField = this.sortState.field === 'naming' ? 'shortName' : this.sortState.field;
        return filteredRows.sort((a, b) => {
            const comparison = naturalSort(String(a[sortField]), String(b[sortField]));
            return this.sortState.direction === 'asc' ? comparison : -comparison;
        });
    }

    get layoutRows() {
        const filtered = this.filteredRows.map((row) =>
            omit(row, ['citiesId', 'cityId', 'undelitable', 'uneditable', 'stoppable']),
        );
        return filtered;
    }

    get hasDeleteAccess(): boolean {
        switch (this.page) {
            case AdministrationPageNamesEnum.Users:
                return hasPermission(PermissionsEnum.UsersDelete);
            case AdministrationPageNamesEnum.Cities:
                return hasPermission(PermissionsEnum.CitiesDelete);
            case AdministrationPageNamesEnum.Replacement:
                return hasPermission(PermissionsEnum.UsersSelfAffil) || hasPermission(PermissionsEnum.UsersAffil);
            case AdministrationPageNamesEnum.Organisations:
            case AdministrationPageNamesEnum.Projects:
            default:
                return false;
        }
    }

    get gridTemplate() {
        return `${this.idColumnWidth} repeat(${this.head.length - 1}, 1fr) ${this.actionsColumnWidth}`;
    }

    public filterBySearch(row: Dictionary<string | number | number[]>) {
        return hasSearch(Object.values(row).join(), this.search);
    }

    public filterByCity(row: Dictionary<string | number | number[]>) {
        const cityIdFiler = isUndefined(row?.cityId) || row.cityId === this.searchCityId;
        const citiesIdFilter =
            isUndefined(row?.citiesId) || (row.citiesId as number[]).find((id) => id === this.searchCityId);

        return this.searchCityId ? Boolean(cityIdFiler && citiesIdFilter) : true;
    }

    public getObserveVisibilityParams() {
        const visibilityChanged = (isVisible: boolean) => {
            if (isVisible) this.$emit('row-visible');
        };

        return { callback: visibilityChanged, once: true };
    }

    public getSortDirection(cell: string) {
        if (
            this.page === AdministrationPageNamesEnum.Projects ||
            this.page === AdministrationPageNamesEnum.Organisations
        ) {
            return this.sortState.field === cell.toLowerCase() ? this.sortState.direction : 'asc';
        }
    }

    public sortRows(cell: string) {
        const sortField = cell.toLowerCase();
        if (this.sortState.field !== sortField) {
            this.sortState.field = sortField;
        }
        this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
    }

    editRow(row: Dictionary<string | number | number[]>) {
        this.$router.push({ name: `administration.${this.page}.edit`, params: { id: row.id.toString() } });
    }
}
